/**
 * FailedImportsSection Component
 *
 * Displays failed raw imports with details and provides reprocess functionality.
 * Shows row number, error message, data preview, and action buttons.
 */

import { useState, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { toast } from 'sonner';
import type { FailedImportsData, ReprocessResult } from '@/types/accounting';

interface FailedImportsSectionProps {
  failedImports: FailedImportsData;
}

/**
 * Reprocess all failed imports via API
 */
async function reprocessAll(): Promise<ReprocessResult> {
  const response = await fetch('/api/accounting/import_config/reprocess', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json() as Promise<ReprocessResult>;
}

/**
 * Reprocess a single failed import via API
 */
async function reprocessOne(id: number): Promise<ReprocessResult> {
  const response = await fetch(`/api/accounting/import_config/reprocess/${id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json() as Promise<ReprocessResult>;
}

/**
 * Truncate a string with ellipsis
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}...`;
}

/**
 * Format raw data array as preview string
 */
function formatRawDataPreview(rawData: unknown[]): string {
  return truncate(JSON.stringify(rawData), 100);
}

/**
 * FailedImportsSection displays failed imports and provides reprocess controls.
 */
export function FailedImportsSection({ failedImports }: FailedImportsSectionProps) {
  const [isReprocessingAll, setIsReprocessingAll] = useState(false);
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);

  const handleReprocessAll = useCallback(async () => {
    setIsReprocessingAll(true);
    try {
      const result = await reprocessAll();
      if (result.success) {
        toast.success('Reprocessing completed', {
          description: `${result.processedCount} processed, ${result.skippedCount} skipped, ${result.failedCount} still failed`,
        });
      } else {
        toast.warning('Reprocessing completed with issues', {
          description: result.errors.length > 0 ? result.errors.join('. ') : `${result.failedCount} items still failed`,
        });
      }
      // Refresh page data using Inertia
      router.reload();
    } catch (error) {
      toast.error('Reprocessing failed', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    } finally {
      setIsReprocessingAll(false);
    }
  }, []);

  const handleReprocessOne = useCallback(async (id: number) => {
    setReprocessingId(id);
    try {
      const result = await reprocessOne(id);
      if (result.success && result.processedCount > 0) {
        toast.success('Import reprocessed successfully');
      } else if (result.failedCount > 0) {
        toast.error('Reprocessing failed', {
          description: result.errors.length > 0 ? result.errors[0] : 'Processing error persists',
        });
      } else {
        toast.info('Import skipped', {
          description: 'Item was skipped during reprocessing',
        });
      }
      // Refresh page data using Inertia
      router.reload();
    } catch (error) {
      toast.error('Reprocessing failed', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    } finally {
      setReprocessingId(null);
    }
  }, []);

  const isReprocessing = isReprocessingAll || reprocessingId !== null;

  // Only render if there are failed imports
  if (failedImports.count === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6">
      {/* Card Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">Failed Imports</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {failedImports.count}
          </span>
        </div>
        <button
          onClick={handleReprocessAll}
          disabled={isReprocessing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isReprocessingAll ? (
            <>
              <LoadingSpinner />
              Reprocessing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reprocess All
            </>
          )}
        </button>
      </div>

      {/* Card Body - Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Row
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Error
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Data Preview
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {failedImports.imports.map((imp) => (
              <tr key={imp.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {imp.rowNumber}
                </td>
                <td className="px-6 py-4 text-sm text-red-600 max-w-xs">
                  <div className="truncate" title={imp.errorMessage}>
                    {imp.errorMessage}
                  </div>
                  {imp.errorClass && (
                    <div className="text-xs text-gray-400 font-mono">
                      {imp.errorClass}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-xs font-mono text-gray-500 max-w-xs">
                  <div className="truncate" title={JSON.stringify(imp.rawData)}>
                    {formatRawDataPreview(imp.rawData)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleReprocessOne(imp.id)}
                    disabled={isReprocessing}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reprocessingId === imp.id ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Retry
                      </>
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer note if showing limited results */}
      {failedImports.count > failedImports.imports.length && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
          Showing {failedImports.imports.length} of {failedImports.count} failed imports
        </div>
      )}
    </div>
  );
}

/**
 * Simple loading spinner component
 */
function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <svg className={`${sizeClasses} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}
